import React, { Fragment, useState } from 'react'
import {
  TableRow,
  TableCell,
  Checkbox,
} from '@mui/material'
import {
  TableColumn,
  SelectionConfig,
  PrioritizationConfig,
  StylingConfig,
  ExpandableConfig,
  TableData,
  TableAction,
  ExpansionTriggerMode
} from '../types'
import { PriorityCell } from './PriorityCell'
import { ActionsCell } from './ActionsCell'
import { ExpandButton } from './ExpandButton'
import { ExpandableContent } from './ExpandableContent'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'

interface TableRowComponentProps<T extends TableData> {
  row: T
  index: number
  columns: TableColumn<T>[]
  keyField: keyof T
  selection?: SelectionConfig<T>
  prioritization?: PrioritizationConfig<T>
  styling?: StylingConfig<T>
  expandable?: ExpandableConfig<T>
  actions?: TableAction<T>[]
  onRowClick?: (row: T, index: number) => void
  onRowDoubleClick?: (row: T, index: number) => void
  // Expandable state management
  rowId?: string | number
  isRowExpanded?: boolean
  hasDetails?: boolean
  onToggleRowExpansion?: () => void
  getDetailData?: (row: T) => any
}

const useStyles = makeStyles((theme: Theme) => ({
  selectedRow: {
    backgroundColor: `${theme.palette.primary.light}20 !important`
  }
}))

export const TableRowComponent = <T extends TableData>({
  row,
  index,
  columns,
  keyField,
  selection,
  prioritization,
  styling,
  expandable,
  actions,
  onRowClick,
  onRowDoubleClick,
  rowId: providedRowId,
  isRowExpanded = false,
  hasDetails = false,
  onToggleRowExpansion,
  getDetailData
}: TableRowComponentProps<T>) => {
  const styles = useStyles()
  const rowId = row[keyField]
  const [isHovered, setIsHovered] = useState(false)

  const isSelected = selection?.selectedIds.includes(rowId) || false
  const isSelectionDisabled = selection?.disabled?.(row) || false

  const handleSelectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (selection && !isSelectionDisabled) {
      const newSelectedIds = event.target.checked
        ? [...selection.selectedIds, rowId]
        : selection.selectedIds.filter(id => id !== rowId)

      selection.onSelectionChange(newSelectedIds)
    }
  }

  // Helper para determinar el modo de expansión efectivo (con retrocompatibilidad)
  const getEffectiveTriggerMode = (): ExpansionTriggerMode => {
    if (expandable?.expansionTriggerMode) {
      return expandable.expansionTriggerMode
    }
    // Retrocompatibilidad: expandOnRowClick legacy
    if (expandable?.expandOnRowClick) {
      return 'row-click'
    }
    return 'button-only'
  }

  // Helper para determinar si debe mostrarse el botón de expandir
  const shouldShowExpandButton = (): boolean => {
    if (!expandable) return false
    const triggerMode = getEffectiveTriggerMode()
    // Solo mostrar botón en modos 'button-only' y 'both'
    return triggerMode === 'button-only' || triggerMode === 'both'
  }

  const handleRowClick = () => {
    const triggerMode = getEffectiveTriggerMode()

    // Si el modo permite expansión por click simple y hay detalles, expandir
    if (
      (triggerMode === 'row-click' || triggerMode === 'both') &&
      hasDetails &&
      onToggleRowExpansion
    ) {
      onToggleRowExpansion()
    }

    onRowClick?.(row, index)
  }

  const handleRowDoubleClick = () => {
    const triggerMode = getEffectiveTriggerMode()

    // Si el modo permite expansión por doble click y hay detalles, expandir
    if (
      (triggerMode === 'double-click' || triggerMode === 'both') &&
      hasDetails &&
      onToggleRowExpansion
    ) {
      onToggleRowExpansion()
    }

    onRowDoubleClick?.(row, index)
  }

  // Calcular el número total de columnas para el colspan del contenido expandible
  const calculateColSpan = (): number => {
    let colSpan = columns.length
    if (selection) colSpan += 1
    if (shouldShowExpandButton()) colSpan += 1
    if (prioritization?.enabled) colSpan += 1
    if (actions && actions.length > 0) colSpan += 1
    return colSpan
  }

  const getRowStyle = (): React.CSSProperties => {
    let style: React.CSSProperties = {
      height: styling?.dense ? 33 : 53
    }

    // Aplicar estilo personalizado de fila
    if (styling?.rowStyle) {
      style = { ...style, ...styling.rowStyle(row, index) }
    }

    // Filas alternadas
    if (styling?.alternatingRows && index % 2 === 1) {
      style.backgroundColor = '#fafafa'
    }

    // Hover effect - aplicar cuando está hovereado y hay hoverEffect habilitado
    if (styling?.hoverEffect) {
      style.cursor = onRowClick ? 'pointer' : 'default'

      if (isHovered) {
        // Si hay un backgroundColor personalizado, oscurecerlo un poco para el hover
        if (style.backgroundColor && style.backgroundColor !== 'white') {
          // Para colores específicos de estados (amarillo, rojo)
          if (style.backgroundColor === '#fff3e0') {
            style.backgroundColor = '#ffcc02' // Amarillo más oscuro para hover
          } else if (style.backgroundColor === '#ffebee') {
            style.backgroundColor = '#ffcdd2' // Rojo más oscuro para hover
          } else {
            // Para otros colores, usar un overlay gris claro
            style.backgroundColor = '#f5f5f5'
          }
        } else {
          // Sin color personalizado, usar gris claro
          style.backgroundColor = '#f5f5f5'
        }
      }
    }

    // Fila seleccionada (tiene mayor prioridad que hover)
    if (isSelected) {
      style.backgroundColor = '#e3f2fd'
    }

    return style
  }

  const renderCellValue = (column: TableColumn<T>) => {
    const value = row[column.key]

    // Si hay un renderer personalizado, usarlo
    if (column.render) {
      return column.render(value, row, index)
    }

    // Renderizado por defecto
    if (typeof value === 'boolean') {
      return value ? 'Activo' : 'Inactivo'
    }

    if (value === null || value === undefined) {
      return '-'
    }

    return String(value)
  }

  const getPriorityNumber = (): number => {
    if (prioritization?.numberField) {
      return row[prioritization.numberField] as number
    }

    if (prioritization?.getRowNumber) {
      return prioritization.getRowNumber(row, index)
    }

    return index + 1
  }

  const uniqueRowId = providedRowId || rowId
  const detailData = getDetailData?.(row)

  return (
    <Fragment>
      <TableRow
        style={getRowStyle()}
        onClick={handleRowClick}
        onDoubleClick={handleRowDoubleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`${isSelected ? styles.selectedRow : ''}`}
        selected={isSelected}
      >
        {selection && (
          <TableCell align="center">
            <Checkbox
              checked={isSelected}
              disabled={isSelectionDisabled}
              onChange={handleSelectionChange}
              onClick={(e) => e.stopPropagation()} // Evitar que se active el onRowClick
            />
          </TableCell>
        )}

        {prioritization?.enabled && (
          <PriorityCell
            rowId={rowId}
            priorityNumber={getPriorityNumber()}
            onMove={prioritization.onMove}
          />
        )}

        {columns.map((column) => (
          <TableCell
            key={String(column.key)}
            align={column.align || 'center'}
            style={column.cellStyle}
          >
            {renderCellValue(column)}
          </TableCell>
        ))}

        {shouldShowExpandButton() && (
          <TableCell align="center">
            <ExpandButton
              expanded={isRowExpanded}
              hasDetails={hasDetails}
              onToggle={onToggleRowExpansion || (() => {})}
              expandable={expandable!}
              row={row}
            />
          </TableCell>
        )}

        {actions && actions.length > 0 && (
          <ActionsCell
            row={row}
            index={index}
            actions={actions}
          />
        )}
      </TableRow>

      {expandable && (
        <ExpandableContent
          expanded={isRowExpanded}
          detailData={detailData}
          row={row}
          expandable={expandable}
          colSpan={calculateColSpan()}
          rowId={uniqueRowId}
        />
      )}
    </Fragment>
  )
}